import React, { useMemo, useRef, useState } from 'react'
import {
    Dialog,
    DialogContent,
    TextField,
    Button,
    IconButton,
    CircularProgress
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { Toast } from 'components/shared/Toaster'
import strings from 'strings'
import { createTask } from 'actions/collaborativeTasks'
import { useDirectorySuggestions } from '../hooks/useDirectorySuggestions'
import { isEmailFormatValid } from '../ctHelpers'


const NewTaskModal = ({ open, onClose, applicationUuid, applicationRef, onCreated }) => {
    const t = strings.CT
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [email, setEmail] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [errors, setErrors] = useState({})
    const inFlight = useRef(false)

    const { suggestions, loading: suggestLoading } = useDirectorySuggestions(applicationUuid, email)

    const externalGuess = useMemo(() => {
        // Show the external warning when the email is a valid format AND no
        // directory hit currently matches it case-insensitively.
        if (!isEmailFormatValid(email)) return false
        const hit = suggestions.some(s => s.email?.toLowerCase() === email.trim().toLowerCase())
        return !hit
    }, [email, suggestions])

    const reset = () => {
        setTitle(''); setDescription(''); setEmail('')
        setErrors({}); setSubmitting(false)
        inFlight.current = false
    }

    const handleClose = () => { reset(); onClose() }

    const handlePickSuggestion = (suggestion) => {
        setEmail(suggestion.email)
    }

    const handleSubmit = async () => {
        const nextErrors = {}
        if (!title.trim()) nextErrors.title = t.REQUIRED_TITLE
        if (!email.trim()) nextErrors.email = t.REQUIRED_ASSIGNEE
        else if (!isEmailFormatValid(email)) nextErrors.email = t.INVALID_EMAIL
        if (Object.keys(nextErrors).length) { setErrors(nextErrors); return }

        if (inFlight.current) return
        inFlight.current = true
        setSubmitting(true)
        try {
            const res = await createTask(applicationUuid, {
                title: title.trim(),
                description: description.trim() || null,
                assigneeEmail: email.trim()
            })()
            Toast({ type: 'success', message: t.CREATED_OK })
            onCreated && onCreated(res?.response || res)
            handleClose()
        } catch (e) {
            Toast({ type: 'error', message: t.CREATED_ERR })
        } finally {
            setSubmitting(false)
            inFlight.current = false
        }
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth classes={{ paper: 'ct-modal-paper' }}>
            <DialogContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        {applicationRef && <div className="ct-modal-header-eyebrow">{applicationRef}</div>}
                        <div className="ct-modal-title">{t.MODAL_NEW_TITLE}</div>
                    </div>
                    <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
                </div>

                <label className="ct-field-label ct-field-label__required">{t.FIELD_TITLE}</label>
                <TextField
                    variant="standard"
                    fullWidth
                    placeholder={t.FIELD_TITLE_PLACEHOLDER}
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    error={!!errors.title}
                    helperText={errors.title}
                    inputProps={{ maxLength: 512 }}
                />

                <label className="ct-field-label">{t.FIELD_DESCRIPTION}</label>
                <TextField
                    variant="standard"
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder={t.FIELD_DESCRIPTION_PLACEHOLDER}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    inputProps={{ maxLength: 4000 }}
                />

                <label className="ct-field-label ct-field-label__required">{t.FIELD_ASSIGN_TO}</label>
                <TextField
                    variant="standard"
                    fullWidth
                    placeholder={t.FIELD_ASSIGN_TO_PLACEHOLDER}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    InputProps={{
                        endAdornment: externalGuess
                            ? <span className="ct-ext-badge">{t.EXT_BADGE}</span>
                            : null
                    }}
                />

                {externalGuess && (
                    <div className="ct-ext-warning">
                        <InfoOutlinedIcon style={{ fontSize: 16, color: '#5B21B6', flexShrink: 0, marginTop: 1 }} />
                        <span>{t.EXT_WARNING}</span>
                    </div>
                )}

                {(suggestLoading || suggestions.length > 0) && (
                    <div className="ct-suggestions">
                        <div className="ct-field-label" style={{ marginTop: 0 }}>{t.SUGGESTIONS_HEADER}</div>
                        {suggestLoading && <CircularProgress size={16} style={{ color: '#7B61FF' }} />}
                        {!suggestLoading && suggestions.map(s => (
                            <div key={s.email} className="ct-suggestion-row" onClick={() => handlePickSuggestion(s)}>
                                <div>
                                    <div className="ct-suggestion-name">{s.name || s.email}</div>
                                    <div className="ct-suggestion-email">{s.email}</div>
                                </div>
                                {s.roleLabel && <div className="ct-suggestion-role">{s.roleLabel}</div>}
                            </div>
                        ))}
                    </div>
                )}

                <div className="ct-modal-footer">
                    <span className="ct-modal-footer__hint">{t.FOOTER_NOTIFICATION_HINT}</span>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Button onClick={handleClose} disabled={submitting}>{t.ACTION_CANCEL}</Button>
                        <Button
                            variant="contained"
                            className="ct-new-task-btn"
                            onClick={handleSubmit}
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={14} style={{ color: '#fff' }} /> : null}
                        >
                            {t.ACTION_CREATE}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default NewTaskModal
